import React, { useState, useMemo } from 'react';
import {
  COLLAPSIBLE_MONTHS,
  REGULAR_MONTHS,
  groupSchedule,
  renderGameSlot,
} from './scheduleUtils';

function ScheduleCollapsible({ title, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className={`calendar-row schedule-collapsible ${open ? 'is-open' : ''}`}>
      <div className="calendar-month schedule-collapsible-panel">
        <button
          type="button"
          className="schedule-collapsible-trigger month-header"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
        >
          <span>{title}</span>
          <i className="fas fa-chevron-down schedule-collapsible-icon" aria-hidden="true" />
        </button>
        <div className="schedule-collapsible-content">{children}</div>
      </div>
    </div>
  );
}

function renderMonthBlock(month, games, scheduleNotes) {
  const monthGames = games || [];
  const footerNote =
    month === 'TBC'
      ? scheduleNotes?.tbc
      : month === 'Special Events'
        ? scheduleNotes?.specialEvents
        : null;

  if (COLLAPSIBLE_MONTHS.includes(month)) {
    return (
      <ScheduleCollapsible key={month} title={month}>
        <div className="month-games">
          {monthGames.map(renderGameSlot)}
        </div>
        {footerNote && <p className="tbc-note">{footerNote}</p>}
      </ScheduleCollapsible>
    );
  }

  return (
    <div className="calendar-month" key={month}>
      <div className="month-header">{month}</div>
      <div className="month-games">
        {monthGames.map(renderGameSlot)}
      </div>
    </div>
  );
}

export default function ScheduleCalendar({ section, schedule = [] }) {
  const [scheduleYear, setScheduleYear] = useState('2026');

  const grouped = useMemo(() => groupSchedule(schedule), [schedule]);
  const years = useMemo(() => Object.keys(grouped).sort(), [grouped]);

  const activeYear = useMemo(() => {
    if (years.includes(scheduleYear)) return scheduleYear;
    if (years.includes('2026')) return '2026';
    return years[years.length - 1] || '2026';
  }, [years, scheduleYear]);

  const scheduleNotes = useMemo(
    () => ({
      tbc: section.tbcNote,
      specialEvents: section.specialEventsNote,
    }),
    [section.tbcNote, section.specialEventsNote],
  );

  if (years.length === 0) {
    return (
      <div className="stream-schedule">
        {section.intro && <p>{section.intro}</p>}
        <p className="schedule-note">No schedule entries yet.</p>
      </div>
    );
  }

  return (
    <div className="stream-schedule">
      {section.intro && <p>{section.intro}</p>}

      <div className="schedule-year-tabs shop-tabs">
        {years.map((year) => (
          <button
            key={year}
            type="button"
            className={`shop-tab ${activeYear === year ? 'active' : ''}`}
            onClick={() => setScheduleYear(year)}
          >
            {year}
          </button>
        ))}
      </div>

      {years.map((year) => {
        const monthsForYear = grouped[year] || {};
        const extraMonths = COLLAPSIBLE_MONTHS.filter((m) => monthsForYear[m]?.length);
        return (
          <div key={year} className={`shop-tab-content ${activeYear === year ? 'active' : ''}`}>
            <div className="schedule-calendar">
              <div className="calendar-row">
                {REGULAR_MONTHS.map((month) =>
                  renderMonthBlock(month, monthsForYear[month], scheduleNotes),
                )}
              </div>
              {extraMonths.length > 0 &&
                extraMonths.map((month) =>
                  renderMonthBlock(month, monthsForYear[month], scheduleNotes),
                )}
            </div>
            {section.footnote && (
              <p className="schedule-note">
                <strong>Note:</strong> {section.footnote}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );
}
